/**
 * Shared types and helpers for the CRM screens.
 *
 * Spend figures arrive as Decimal *strings* (`totalSpend`, `averageSpend`) and
 * are handed to `formatCurrency` untouched — never parsed to a float.
 */
import {
  ConsentStatus,
  ContactChannel,
  CouponStatus,
  CustomerMergeStatus,
  FeedbackStatus,
  HUMAN_CONSENT_SOURCES,
  PersonalDataSource,
  PromotionType,
  SegmentMatch,
  SegmentOperator,
  SegmentRefreshMode,
} from '@/lib/shared-types';

/**
 * X-12 — `FeedbackStatus`, `CouponStatus` and `PromotionType` are Prisma enums
 * and now live in `@/lib/shared-types`. They are re-exported here so the CRM
 * tabs keep one import, but there is no second definition to drift.
 */
export {
  ConsentStatus,
  ContactChannel,
  CouponStatus,
  CustomerMergeStatus,
  FeedbackStatus,
  HUMAN_CONSENT_SOURCES,
  PersonalDataSource,
  PromotionType,
  SegmentMatch,
  SegmentOperator,
  SegmentRefreshMode,
};

export type CustomerSegment = 'NEW' | 'RETURNING' | 'REGULAR' | 'VIP' | 'LAPSED';

export const SEGMENTS: CustomerSegment[] = ['NEW', 'RETURNING', 'REGULAR', 'VIP', 'LAPSED'];

export interface LoyaltyAccount {
  id: string;
  customerId: string;
  points: number;
  tier: string | null;
}

export interface LoyaltyLedgerEntry {
  id: string;
  accountId: string;
  points: number;
  reason: string;
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

export interface LoyaltyDetail extends LoyaltyAccount {
  /** Re-aggregated from the append-only ledger; drift against `points` is visible. */
  ledgerBalance: number;
  recentLedger: LoyaltyLedgerEntry[];
}

export interface CustomerRow {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  locale: string;
  notes: string | null;
  createdAt: string;
  visits: number;
  totalSpend: string;
  averageSpend: string;
  lastVisitAt: string | null;
  segment: CustomerSegment;
}

export interface OrderHistoryEntry {
  id: string;
  number: string;
  status: string;
  fulfillmentType: string;
  total: string;
  placedAt: string | null;
  createdAt: string;
  _count?: { items: number };
}

export interface FavouriteProduct {
  itemId: string;
  name: string | null;
  type: string | null;
  timesOrdered: number;
  totalQuantity: string;
}

/**
 * `GET /customers/:id`.
 *
 * `visitHistory` is gone. A visit used to be a `PartyParticipant` row carrying
 * `customerId`, and Party took that link with it; the API now counts a visit as
 * an ORDER carrying this customer's phone number, and `visits`, `totalSpend`,
 * `averageSpend` and `lastVisitAt` are all computed from those orders. So the
 * order history below IS the visit history — one list rather than two views of
 * the same thing, and the one the counter actually collects an identifier for.
 */
export interface CustomerDetail extends CustomerRow {
  loyalty: LoyaltyAccount | null;
  orderHistory: OrderHistoryEntry[];
  favouriteProducts: FavouriteProduct[];
  reservations: Array<{
    id: string;
    startsAt: string;
    status: string;
    partySize: number | null;
  }>;
}

export const FEEDBACK_STATUSES: FeedbackStatus[] = Object.values(FeedbackStatus);

export interface Feedback {
  id: string;
  customerId: string | null;
  branchId: string | null;
  assigneeId: string | null;
  rating: number;
  foodRating: number | null;
  serviceRating: number | null;
  speedRating: number | null;
  comment: string | null;
  status: FeedbackStatus;
  isServiceRecovery: boolean;
  resolutionNote: string | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  customer?: { id: string; name: string | null; phone: string | null } | null;
  assignee?: { id: string; firstName: string | null; lastName: string | null } | null;
}

export const COUPON_STATUSES: CouponStatus[] = Object.values(CouponStatus);

export const PROMOTION_TYPES: PromotionType[] = Object.values(PromotionType);

export interface Coupon {
  id: string;
  code: string;
  promotionId: string | null;
  customerId: string | null;
  status: CouponStatus;
  discountType: PromotionType | null;
  discountValue: string | null;
  minSpend: string | null;
  maxRedemptions: number;
  redemptionCount: number;
  startsAt: string | null;
  expiresAt: string | null;
  redeemedAt: string | null;
  createdAt: string;
}

export interface Promotion {
  id: string;
  name: string;
  description: string | null;
  type: PromotionType;
  value: string;
  minSpend: string | null;
  maxDiscount: string | null;
  branchId: string | null;
  daysOfWeek: number[];
  startTime: string | null;
  endTime: string | null;
  startsAt: string | null;
  endsAt: string | null;
  requiresCoupon: boolean;
  priority: number;
  isActive: boolean;
}

/** Surface the API's own message rather than a generic failure line. */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

/**
 * `response?.status`, so a screen can tell a 409 ("nothing has run yet" /
 * "this row moved under you") apart from every other failure. Never used to
 * decide whether something failed — only to decide *which* failure state to
 * draw, per the empty-vs-broken rule.
 */
export function apiStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// =============================================================================
// ADR-0010 — Customer 360, consent, tags, duplicate merge, privacy, segments.
// Pinned from `/tmp/integrate` (branch `integrate/wave-a`), read-only source:
//   - src/modules/customers/crm/*.service.ts + customer-crm.controller.ts
//   - src/modules/customers/dto/*.ts
//   - src/modules/segments/*.ts + segments/dto/segment.dto.ts
// One file, per the house rule of pinning every consumed API type in one
// place per module, so a later drift in the backend is a one-file diff here.
// =============================================================================

// --------------------------------------------------------------- consent

/** `GET /customers/:id/consent` — one entry per channel, always all four. */
export interface ChannelConsent {
  channel: ContactChannel;
  /** `null` means no row exists — never rendered as a fifth status. */
  status: ConsentStatus | null;
  source: PersonalDataSource | null;
  evidence: string | null;
  capturedAt: string | null;
  /** The only field a "may we message them" check should ever read. */
  contactable: boolean;
}

/** `GET /customers/:id/consent/history` — append-only, newest first. */
export interface ConsentEvent {
  id: string;
  channel: ContactChannel;
  status: ConsentStatus;
  previousStatus: ConsentStatus | null;
  source: PersonalDataSource;
  evidence: string | null;
  actorId: string | null;
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

/** Body of `PUT /customers/:id/consent`. Only the four human sources are offered. */
export interface RecordConsentInput {
  channel: ContactChannel;
  status: ConsentStatus;
  source: PersonalDataSource;
  evidence?: string;
  /** ISO 8601. When the guest actually decided, which an import can backdate. */
  capturedAt?: string;
}

// ------------------------------------------------------------------- tags

/** `GET /customer-tags` row, and what `GET /customers/:id/tags` returns per entry. */
export interface CustomerTag {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  description: string | null;
  colour: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { assignments: number };
}

export interface CreateCustomerTagInput {
  name: string;
  description?: string;
  colour?: string;
}

// ----------------------------------------------------------------- merge

/** One row of `customer_merges`, as returned by `findMerge` / `history` / `merge`. */
export interface CustomerMergeRecord {
  id: string;
  tenantId: string;
  winnerId: string;
  loserId: string;
  status: CustomerMergeStatus;
  /** Signed from the loser's point of view: what left it and arrived on the winner. */
  pointsMoved: number;
  /** Row counts per table this merge touched, plus `reversible`. Read via `mergeCounts()`. */
  moved: Record<string, number | boolean> | null;
  reason: string | null;
  snapshotRedactedAt: string | null;
  mergedById: string | null;
  reversedById: string | null;
  reversedAt: string | null;
  createdAt: string;
  /** Present only on `GET /customers/:id/merges` (`history`), which includes both rows. */
  winner?: { id: string; name: string | null; phone: string | null };
  loser?: { id: string; name: string | null; phone: string | null };
}

/** `moved` typed for display; every count is `>= 0` and `reversible` says whether a snapshot was truncated. */
export function mergeCounts(merge: CustomerMergeRecord): {
  reservations: number;
  feedback: number;
  coupons: number;
  guardians: number;
  notifications: number;
  tagAssignments: number;
  segmentMembershipsDropped: number;
  reattachedTombstones: number;
  reversible: boolean;
} {
  const m = merge.moved ?? {};
  const n = (key: string) => (typeof m[key] === 'number' ? (m[key] as number) : 0);
  return {
    reservations: n('reservations'),
    feedback: n('feedback'),
    coupons: n('coupons'),
    guardians: n('guardians'),
    notifications: n('notifications'),
    tagAssignments: n('tagAssignments'),
    segmentMembershipsDropped: n('segmentMembershipsDropped'),
    reattachedTombstones: n('reattachedTombstones'),
    reversible: m.reversible !== false,
  };
}

// ------------------------------------------------------------------- 360

/**
 * `GET /customers/:id/360`. A superset of the plain `CustomerDetail` above,
 * assembled for the CRM screen only — `GET /customers/:id` (POS, reservations)
 * is untouched and keeps its own shape.
 */
export interface Customer360 {
  customer: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    locale: string;
    notes: string | null;
    birthDate: string | null;
    birthDateSource: PersonalDataSource | null;
    createdAt: string;
    erasedAt: string | null;
  };
  identity: {
    primaryPhone: string | null;
    /** Phone numbers of every tombstone merged into this customer. */
    mergedPhones: string[];
    mergedInto: string | null;
    mergedAt: string | null;
  };
  consent: ChannelConsent[];
  tags: CustomerTag[];
  stats: {
    visits: number;
    totalSpend: string;
    averageSpend: string;
    firstVisitAt: string | null;
    lastVisitAt: string | null;
    recencyDays: number | null;
    currency: string;
  };
  favouriteBranch: { branchId: string; visits: number } | null;
  favouriteItems: Array<{
    itemId: string;
    name: string | null;
    type: string | null;
    categoryId: string | null;
    timesOrdered: number;
    totalQuantity: string;
  }>;
  channelMix: Array<{ fulfillmentType: string; visits: number }>;
  loyalty: { points: number; tier: string | null; ledgerBalance: number } | null;
  recentOrders: OrderHistoryEntry[];
  reservations: Array<{
    id: string;
    startsAt: string;
    status: string;
    partySize: number | null;
    tableId: string | null;
    resourceId: string | null;
  }>;
  feedback: Array<{
    id: string;
    rating: number;
    comment: string | null;
    status: FeedbackStatus;
    isServiceRecovery: boolean;
    branchId: string | null;
    createdAt: string;
  }>;
  coupons: Array<{
    id: string;
    code: string;
    status: CouponStatus;
    redeemedAt: string | null;
    expiresAt: string | null;
    promotionId: string | null;
  }>;
  /** Membership in the NEW predicate model (ADR-0010 §3) — never the fixed ladder badge. */
  segments: Array<{ id: string; name: string; evaluatedAt: string }>;
  merges: CustomerMergeRecord[];
}

/**
 * The history arrays above are each capped at 20 (`HISTORY_LIMIT` in
 * `Customer360Service`), so "20 shown" cannot be told apart from "exactly 20
 * happened" from the length alone. A merge preview that needs an honest count
 * of what will move must say "20+" rather than "20" when a list is at the cap.
 */
export const CUSTOMER_360_HISTORY_LIMIT = 20;

// --------------------------------------------------------------- privacy

/** `GET /customers/:id/export` — the 360 payload plus the full consent event log. */
export interface CustomerExport extends Customer360 {
  exportedAt: string;
  consentHistory: ConsentEvent[];
}

export const ERASURE_CONFIRMATION = 'ERASE';

// -------------------------------------------------------------- segments

/** `GET /segments/vocabulary` — served, never hardcoded, so the rule editor cannot offer a combination the validator would reject. */
export interface SegmentVocabularyField {
  field: string;
  operators: SegmentOperator[];
  valueKind: 'number' | 'money' | 'idList' | 'enumList' | 'monthList' | 'consent' | 'presence';
  /** Which tenant-owned table an `idList` resolves against, or null. */
  reference: 'branch' | 'catalogItem' | 'category' | 'tag' | null;
  /** The closed value set for an `enumList`/`consent` field, or null when it is open (e.g. `TAG`, `LOYALTY_TIER`). */
  allowedValues: string[] | null;
}

export interface SegmentVocabulary {
  match: SegmentMatch[];
  maxRules: number;
  fields: SegmentVocabularyField[];
}

/** One rule inside a `SegmentDefinition`. Mirrors `SegmentRule` in `segment-definition.ts` exactly. */
export interface SegmentRule {
  field: string;
  operator: SegmentOperator;
  /** Numeric/money value, or the status for a CONSENT rule. Money is always a decimal STRING. */
  value?: string | number;
  /** IN / NOT_IN lists, and BIRTHDAY_MONTH's month numbers. */
  values?: Array<string | number>;
  /** CONSENT only. */
  channel?: ContactChannel;
}

export interface SegmentDefinition {
  match: SegmentMatch;
  rules: SegmentRule[];
}

/** A saved segment, as listed or fetched — always carries its own freshness. */
export interface Segment {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  definition: SegmentDefinition;
  refreshMode: SegmentRefreshMode;
  isActive: boolean;
  lastEvaluatedAt: string | null;
  lastEvaluationMs: number | null;
  lastEvaluationError: string | null;
  memberCount: number;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  /** `lastEvaluatedAt` older than the sweep interval. Computed server-side; never re-derived here. */
  stale: boolean;
  /** Explicit rather than inferred from a null timestamp — "never run" and "stale" read differently. */
  neverEvaluated: boolean;
}

export interface CreateSegmentInput {
  name: string;
  description?: string;
  definition: SegmentDefinition;
  refreshMode?: SegmentRefreshMode;
  isActive?: boolean;
}

export type UpdateSegmentInput = Partial<CreateSegmentInput>;

/** `POST /segments/preview` — bounded, on-demand, for an unsaved definition. */
export interface SegmentPreviewResult {
  memberCount: number;
  candidates: number;
  durationMs: number;
  sample: Array<{ id: string; name: string | null; phone: string | null; email: string | null }>;
}

/** One row of `GET /segments/:id/members`. */
export interface SegmentMemberRow {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  locale: string;
  evaluatedAt: string;
}

/** `GET /segments/:id/members` — a paginated list wrapped in the freshness contract §3.2 asks for. */
export interface SegmentMembersPage {
  data: SegmentMemberRow[];
  meta: { total: number; page: number; limit: number };
  segment: Segment;
  evaluatedAt: string | null;
  stale: boolean;
  contactableOn: ContactChannel | null;
}

/** `GET /segments/:id/contactable?channel=`. */
export interface SegmentContactableResult {
  channel: ContactChannel;
  memberCount: number;
  contactableCount: number;
  evaluatedAt: string;
  stale: boolean;
}

/**
 * Client-side mirror of `ConsentService.reconcile` (ADR-0010 §4), for the merge
 * preview ONLY — the server is the actual authority and re-derives this itself
 * inside the merge transaction. A refusal on either side always wins; absent a
 * refusal, the more recently captured state wins; an absent row loses to any
 * present one.
 */
export function previewConsentReconciliation(
  winner: ChannelConsent,
  loser: ChannelConsent,
): ConsentStatus | null {
  const refusal = [winner, loser].find(
    (c) => c.status === ConsentStatus.DENIED || c.status === ConsentStatus.WITHDRAWN,
  );
  if (refusal) return ConsentStatus.WITHDRAWN;
  if (!winner.status) return loser.status;
  if (!loser.status) return winner.status;
  if (!winner.capturedAt) return loser.status;
  if (!loser.capturedAt) return winner.status;
  return loser.capturedAt > winner.capturedAt ? loser.status : winner.status;
}
